import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import { apiErrorToast } from "Utils";
import { useUpdateReportLinkMutation } from "../../../services/sharedReports.api";
import { SharedReportLinkNameForm } from "../../../types";
import { getSharedReportLinkNameSchema } from "../../../utils";

const schema = getSharedReportLinkNameSchema();

type RenameReportLinkDialogProps = {
  purl: string;
  onClose: () => void;
  isOpen: boolean;
  currentLinkName: string;
  token: string;
};

const RenameReportLinkDialogContent = ({
  purl,
  onClose,
  currentLinkName,
  token,
}: Omit<RenameReportLinkDialogProps, "isOpen">): React.JSX.Element => {
  const [updateReportLink] = useUpdateReportLinkMutation();
  const formMethods = useForm<SharedReportLinkNameForm>({
    resolver: yupResolver(schema),
    defaultValues: { label: currentLinkName },
  });

  const { handleSubmit, formState } = formMethods;

  const renameLink = async (formData: SharedReportLinkNameForm) => {
    try {
      await updateReportLink({
        token_key: token,
        body: {
          label: formData.label,
        },
      }).unwrap();

      Toast.show({
        title: "The link name has been changed successfully",
        type: "success",
      });

      onClose();
    } catch (error) {
      apiErrorToast(error, "Could not generate link");
      return;
    }
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          Rename <em>{currentLinkName}</em>
        </Dialog.Header.Title>
        <Dialog.Header.Subtitle>
          <code>{purl}</code>
        </Dialog.Header.Subtitle>
      </Dialog.Header>
      <FormProvider {...formMethods}>
        <Form.Form onSubmit={handleSubmit(renameLink)}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Content>
                <Form.TextField
                  label="Edit link name"
                  name="label"
                  id="label"
                  errors={formState.errors}
                  isFullWidth
                  isRequired
                  placeholder={currentLinkName}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button color="primary" type="submit">
                Save
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const RenameReportLinkDialog = ({
  isOpen,
  ...props
}: RenameReportLinkDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <RenameReportLinkDialogContent {...props} />
  </Dialog.Frame>
);

export default RenameReportLinkDialog;
